import { useState, useRef } from "react";
import Button from "@mui/material/Button";
import MenuContainer from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreIcon from "@mui/icons-material/MoreVert";
import Typography from "@mui/material/Typography";
import { Theme, useMediaQuery } from "@mui/material";
import { typography } from "../../../../../utils/typography";
import { semanticColors } from "../../../../../utils/colors";
import { useTranslation } from "react-i18next";

import AboutDialog from "../../../AboutDialog/AboutDialog";
import ConnectionOptionsDialog from "../../../ConnectionOptionsDialog/ConnectionOptionsDialog";
import DeviceSelectionDialog from "../../../DeviceSelectionDialog/DeviceSelectionDialog";
import SettingsIcon from "../../../../../icons/SettingsIcon";
import { useVideoState } from "../../../../../state";

export default function SettingsMenu({
  mobileButtonClass
}: {
  mobileButtonClass?: string;
  // Kept for API compatibility with callers; the invite flow was removed
  // (the copy-link action already lives on the video card).
  roomName?: string;
}) {
  const { t } = useTranslation();
  const { roomType } = useVideoState();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [deviceSettingsOpen, setDeviceSettingsOpen] = useState(false);
  const [connectionSettingsOpen, setConnectionSettingsOpen] = useState(false);

  const anchorRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {isMobile ? (
        <Button
          ref={anchorRef}
          onClick={() => setMenuOpen(true)}
          startIcon={<MoreIcon />}
          className={mobileButtonClass}
          sx={{
            ...typography.body.l,
            color: semanticColors.text.primary,
            textTransform: "none"
          }}
        >
          {t("settings.more")}
        </Button>
      ) : (
        <Button
          ref={anchorRef}
          onClick={() => setMenuOpen(true)}
          startIcon={<SettingsIcon />}
          sx={{
            ...typography.body.l,
            color: semanticColors.text.primary,
            textTransform: "none"
          }}
        >
          {t("settings.title")}
        </Button>
      )}
      <MenuContainer
        open={menuOpen}
        onClose={() => setMenuOpen(isOpen => !isOpen)}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "top",
          horizontal: isMobile ? "left" : "right"
        }}
        transformOrigin={{
          vertical: 0,
          horizontal: "center"
        }}
      >
        <MenuItem onClick={() => setAboutOpen(true)}>
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {t("settings.about")}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => setDeviceSettingsOpen(true)}>
          <Typography
            sx={{ ...typography.body.l, color: semanticColors.text.primary }}
          >
            {t("settings.audioVideo")}
          </Typography>
        </MenuItem>
        {roomType !== "peer-to-peer" && roomType !== "go" && (
          <MenuItem onClick={() => setConnectionSettingsOpen(true)}>
            <Typography
              sx={{ ...typography.body.l, color: semanticColors.text.primary }}
            >
              {t("settings.connection")}
            </Typography>
          </MenuItem>
        )}
      </MenuContainer>
      <AboutDialog
        open={aboutOpen}
        onClose={() => {
          setAboutOpen(false);
          setMenuOpen(false);
        }}
      />
      <DeviceSelectionDialog
        open={deviceSettingsOpen}
        onClose={() => {
          setDeviceSettingsOpen(false);
          setMenuOpen(false);
        }}
      />
      <ConnectionOptionsDialog
        open={connectionSettingsOpen}
        onClose={() => {
          setConnectionSettingsOpen(false);
          setMenuOpen(false);
        }}
      />
    </>
  );
}
